import { FloatingPortal, Placement } from '@floating-ui/react';
import { clsx } from 'helpers/utils/HTMLUtils';
import useEvent from 'hooks/useEvent';
import useFloatingPanel from 'hooks/useFloatingPanel';
import useOutsideClick from 'hooks/useOutsideClick';
import { HTMLAttributes, memo, useState } from 'react';

import './floatingPanel.scss';
const rootClassName = 'floating-panel';

export interface FloatingPanelProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * store your ref in state, like:
   * const [ref, setRef] = useState<HTMLDivElement>();
   * and
   * <div ref={(e) => setRef(e)} />
   */
  statedAnchorRef: HTMLElement | undefined | null;
  opened: boolean;
  setOpened: (evt: boolean) => void;
  matchWidth?: boolean;
  placement?: Placement;
  fallbackPlacements?: Placement[];
  offset?: number;
  closeOnOutClick?: boolean;
  closeOnEsc?: boolean;
  heightAvaliableCallback?: (height: number) => void;
}

function FloatingPanel({
  opened,
  setOpened,
  className = '',
  statedAnchorRef,
  matchWidth,
  placement = 'bottom',
  closeOnEsc = true,
  closeOnOutClick = true,
  style = {},
  fallbackPlacements = [
    'bottom',
    'bottom-end',
    'bottom-start',
    'top',
    'top-start',
    'top-end',
    'left',
    'right',
  ],
  offset = 0,
  heightAvaliableCallback,
  ...props
}: FloatingPanelProps) {
  const [panelRef, setPanelRef] = useState<HTMLElement | null>();
  const { isMounted, panelStyles } = useFloatingPanel({
    stateOpened: [opened, setOpened],
    strategy: 'fixed',
    placement,
    fallbackPlacements,
    matchWidth,
    elements: { floating: panelRef, reference: statedAnchorRef },
    heightAvaliableCallback,
    offset,
  });

  const classNames = clsx([rootClassName, className]);

  useEvent([
    {
      event: 'keydown',
      handler: (e) => {
        if (closeOnEsc && opened && 'key' in e && e.key === 'Escape') {
          setOpened(false);
          e.preventDefault();
          e.stopPropagation();
        }
      },
    },
  ]);

  useOutsideClick(
    [{ current: panelRef ?? null }, { current: statedAnchorRef ?? null }],
    (isInside) => {
      if (closeOnOutClick && opened && !isInside) setOpened(false);
    }
  );

  return (
    <FloatingPortal>
      {isMounted && (
        <div
          {...props}
          className={classNames}
          ref={(r) => setPanelRef(r)}
          style={{ ...style, ...panelStyles }}
        >
          {props.children}
        </div>
      )}
    </FloatingPortal>
  );
}

export default memo(FloatingPanel);
